import React from 'react';

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Animate a number from 0 toward `target` once on mount, easing out so it
 * decelerates into its final value. `delay` staggers the start so a row of
 * figures can count up in sequence.
 *
 * Drives the Pulse "Business Value" headline (mirrors the source dashboard's
 * `StatValue` count-up). requestAnimationFrame timestamps drive the easing — no
 * timers in the animation loop — matching the area's other entrance animations
 * (see `ScoreGauge`). Honours `prefers-reduced-motion` by snapping to `target`.
 */
export const useCountUp = (target: number, duration = 950, delay = 0): number => {
  const [display, setDisplay] = React.useState(0);

  React.useEffect(() => {
    const reduce =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDisplay(target);
      return;
    }

    let raf = 0;
    let start: number | null = null;
    const tick = (now: number): void => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / duration);
      setDisplay(target * easeOutCubic(t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    const timer = window.setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, delay);

    return () => {
      window.clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target, duration, delay]);

  return display;
};

export default useCountUp;
